import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtDate } from "@/lib/api";
import { Plus, Check, Trash, X, Wrench, MagnifyingGlass } from "@phosphor-icons/react";
import HelpLink from "@/components/HelpLink";

const emptyTask = { title: "", due_date: "", recurrence: "none", notes: "", completed: false };

export default function Maintenance() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyTask);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/maintenance", { params: q ? { q } : {} })
      .then((r) => setTasks(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const h = setTimeout(load, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const save = async () => {
    if (!form.title || !form.due_date) {
      toast.error("Title and due date required"); return;
    }
    try {
      await api.post("/maintenance", form);
      toast.success("Task logged.");
      setModal(false); setForm(emptyTask); load();
    } catch { toast.error("Save failed."); }
  };

  const toggleComplete = async (t) => {
    await api.put(`/maintenance/${t.id}`, { ...t, completed: !t.completed });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Remove task?")) return;
    await api.delete(`/maintenance/${id}`);
    load();
  };

  const isOverdue = (d, completed) => {
    if (completed) return false;
    try { return new Date(d) < new Date(new Date().toDateString()); } catch { return false; }
  };

  return (
    <div data-testid="maintenance-page" className="space-y-8">
      <header className="flex items-end justify-between border-b border-[#C9C4B8] pb-6">
        <div>
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2 flex items-center gap-3">
          <span>Operational Vault · Part II</span>
          <HelpLink anchor="maintenance" />
        </div>
          <h1 className="font-serif-display text-5xl text-[#0A192F] leading-none">Maintenance Log.</h1>
          <p className="text-sm text-[#5C5F58] mt-3 max-w-xl">
            Shift from reactive crisis to planned upkeep. Seasonal roof inspections, gear lubrication, filter replacements — every recurrence, on record.
          </p>
        </div>
        <button
          data-testid="add-task-btn"
          onClick={() => setModal(true)}
          className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-5 py-2.5 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818]"
        >
          <Plus size={16} weight="bold" /> Add Task
        </button>
      </header>

      <div className="flex justify-end" data-testid="maintenance-search-row">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5F58]" />
          <input
            data-testid="maintenance-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search tasks…"
            className="bg-white border border-[#E0DDD6] pl-9 pr-3 py-2 text-sm w-64 focus:outline-none focus:border-[#1A3622]"
          />
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-[#5C5F58]">Loading…</p>
      ) : tasks.length === 0 ? (
        <div className="bg-white border border-[#E0DDD6] px-8 py-16 text-center" data-testid="maintenance-empty">
          <Wrench size={28} color="#5C5F58" className="mx-auto mb-4" />
          <p className="font-serif-display text-3xl text-[#0A192F] mb-2">No tasks scheduled.</p>
          <p className="text-sm text-[#5C5F58] max-w-md mx-auto">
            Add a seasonal task — roof inspection, HVAC filter change, gear maintenance. The ledger will remind you so you don&apos;t have to remember.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#E0DDD6]" data-testid="tasks-list">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F8F6] border-b border-[#E0DDD6]">
              <tr>
                <th className="w-10" />
                <th className="px-4 py-3 text-left font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58]">Task</th>
                <th className="px-4 py-3 text-left font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58]">Due</th>
                <th className="px-4 py-3 text-left font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58]">Recurrence</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => (
                <tr
                  key={t.id}
                  className={`border-b border-[#E0DDD6] ${t.completed ? "opacity-50" : ""} ${isOverdue(t.due_date, t.completed) ? "bg-[#6B1718]/5" : ""}`}
                  data-testid={`task-${t.id}`}
                >
                  <td className="pl-4 py-3">
                    <button
                      data-testid={`complete-${t.id}`}
                      onClick={() => toggleComplete(t)}
                      className={`w-5 h-5 border flex items-center justify-center ${t.completed ? "bg-[#1A3622] border-[#1A3622]" : "border-[#C9C4B8] hover:border-[#1A3622]"}`}
                    >
                      {t.completed && <Check size={12} color="#F9F8F6" weight="bold" />}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className={`text-[#0A192F] ${t.completed ? "line-through" : ""}`}>{t.title}</div>
                    {t.notes && <div className="text-xs text-[#5C5F58] mt-1">{t.notes}</div>}
                  </td>
                  <td className="px-4 py-3 font-mono-ledger text-sm">
                    <span className={isOverdue(t.due_date, t.completed) ? "text-[#6B1718]" : "text-[#0A192F]"}>
                      {fmtDate(t.due_date)}
                    </span>
                    {isOverdue(t.due_date, t.completed) && (
                      <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-[#6B1718]">Overdue</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono-ledger text-xs text-[#5C5F58] uppercase tracking-wider">{t.recurrence || "none"}</td>
                  <td className="pr-4 py-3">
                    <button onClick={() => remove(t.id)} className="text-[#5C5F58] hover:text-[#6B1718]" data-testid={`delete-task-${t.id}`}>
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 bg-[#0A192F]/40 flex items-center justify-center p-4" data-testid="task-modal">
          <div className="bg-white border border-[#E0DDD6] max-w-lg w-full">
            <header className="flex items-center justify-between px-6 py-4 border-b border-[#E0DDD6]">
              <h2 className="font-serif-display text-2xl text-[#0A192F]">New Maintenance Task</h2>
              <button onClick={() => setModal(false)} data-testid="close-task-modal"><X size={18} /></button>
            </header>
            <div className="p-6 space-y-4">
              <Field label="Title *">
                <input data-testid="task-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full bg-white border border-[#E0DDD6] px-3 py-2 text-sm focus:outline-none focus:border-[#1A3622]" />
              </Field>
              <Field label="Due Date *">
                <input data-testid="task-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  className="w-full bg-white border border-[#E0DDD6] px-3 py-2 text-sm focus:outline-none focus:border-[#1A3622]" />
              </Field>
              <Field label="Recurrence">
                <select data-testid="task-recurrence" value={form.recurrence} onChange={(e) => setForm({ ...form, recurrence: e.target.value })}
                  className="w-full bg-white border border-[#E0DDD6] px-3 py-2 text-sm focus:outline-none focus:border-[#1A3622]">
                  <option value="none">One-time</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="semiannual">Every 6 months</option>
                  <option value="yearly">Yearly</option>
                </select>
              </Field>
              <Field label="Notes">
                <textarea value={form.notes} rows={3} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full bg-white border border-[#E0DDD6] px-3 py-2 text-sm focus:outline-none focus:border-[#1A3622]" />
              </Field>
            </div>
            <footer className="flex justify-end gap-2 px-6 py-4 border-t border-[#E0DDD6] bg-[#F9F8F6]">
              <button onClick={() => setModal(false)} className="px-4 py-2 text-sm border border-[#E0DDD6] bg-white">Cancel</button>
              <button onClick={save} data-testid="save-task" className="px-4 py-2 text-sm bg-[#1A3622] text-[#F9F8F6] hover:bg-[#0F2818]">Save</button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1.5">{label}</span>
      {children}
    </label>
  );
}
